import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { preFillInvestorKycForm } from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import {
	FormPair,
	hasSubmission,
} from '@dealstack/frontend/investor/pages/Investment/common/InvestorKycFormsTable/types';

// Anything submitted or already off the blank template is the investor's copy; the backend
// would no-op, so skip the round trip.
const isAwaitingPreFill = (form: FormPair): boolean =>
	!hasSubmission(form) && form.fileId === form.templateFileId;

/**
 * Pre-fills a KYC form with the investor's data as they open it to complete or sign, and returns
 * the form pointing at the filled copy. Deferred to here because the list is fetched on entering
 * the flow, before the investor has entered the details it reads.
 *
 * A failed request hands back the blank, as the backend does for an unfillable template: a
 * pre-fill must never stand between the investor and their form.
 */
export const usePreFillKycForm = (kycWorkflowId: string) => {
	const queryClient = useQueryClient();

	return useCallback(
		async (form: FormPair): Promise<FormPair> => {
			if (!isAwaitingPreFill(form)) {
				return form;
			}
			const filled = await preFillInvestorKycForm({
				templateId: form.templateId,
				kycWorkflowId,
			})
				.then((response) => response.payload)
				.catch(() => undefined);
			if (!filled) {
				return form;
			}
			void queryClient.invalidateQueries({
				queryKey: [API_PATHS.SHAREHOLDER_KYC_KYC_FORMS_DOCUMENTWORKFLOW_GET, kycWorkflowId],
			});
			return {
				...form,
				fileId: filled.fileId,
				fileName: filled.fileName,
				signatureId: filled.signatureId,
			};
		},
		[kycWorkflowId, queryClient]
	);
};
